/**
 * Product Detail Page
 * Dynamic route for individual product pages
 *
 * FunnelExperience is mounted at this level (NOT inside ProductDetailsPage)
 * so it doesn't unmount/remount when the product loading state changes.
 */

import { useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ProductDetailsPage } from '@/components/organisms/ProductDetailsPage';
import {
  CampaignErrorBoundary,
  CampaignExperience,
  extractFreeGiftItem,
  type RuntimeCampaign,
} from '@template-project/storefront-campaign';
import { FunnelExperience, FunnelErrorBoundary } from '@template-project/storefront-funnel';
import { useFunnelAddToCart } from '@/lib/hooks/use-funnel-add-to-cart';
import { useIntl } from '@/lib/intl';

export function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { localizedPath, countryConfig } = useIntl();
  const handleFunnelAddToCart = useFunnelAddToCart();
  const urlParameters = Object.fromEntries(searchParams.entries());
  const handleCampaignFreeGiftAccept = useCallback(async (campaign: RuntimeCampaign) => {
    const giftItem = extractFreeGiftItem(campaign);
    if (!giftItem || !giftItem.variantId) {
      throw new Error('Free gift campaign is missing product metadata');
    }
    await handleFunnelAddToCart(giftItem.productId, giftItem.variantId, giftItem.quantity);
  }, [handleFunnelAddToCart]);

  if (!slug) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Invalid product URL</p>
        <Link to={localizedPath('/products')} style={{ color: '#f7a08b' }}>← Back to Products</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1rem' }}>
      <CampaignErrorBoundary resetKey={slug}>
        <CampaignExperience
          pageType="product_page"
          context={{
            currentUrl: `/products/${slug}`,
            country: countryConfig.countryCode,
            urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
          }}
          onFreeGiftAccept={handleCampaignFreeGiftAccept}
        />
      </CampaignErrorBoundary>
      <FunnelErrorBoundary resetKey={slug}>
        <FunnelExperience
          pageType="product_page"
          context={{
            productId: slug,
            url: `/products/${slug}`,
            country: countryConfig.countryCode,
            urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
          }}
          onAddToCart={handleFunnelAddToCart}
        />
      </FunnelErrorBoundary>
      <ProductDetailsPage
        productSlug={slug}
        vendor="shopify"
        shop="shapermint"
      />
    </div>
  );
}
